import axios, { AxiosError, AxiosResponse } from 'axios';

import { coreServiceUrl } from '../../../config/environment';
import { ArticleDefinition, BaseArticle } from '../../../interfaces/Article';
import { ArticleImage } from '../../../interfaces/ArticleImage';
import { BlockedSlot, BlockedSlotMapper } from '../../../interfaces/BlockedSlot';
import { Category } from '../../../interfaces/Category';
import { Color } from '../../../interfaces/ColorValue';
import ConnectableSlots, {
  ConnectableSlotsAndRequiresConnectArticle,
} from '../../../interfaces/ConnectableSlots';
import { MenuProduct } from '../../../interfaces/MenuProduct';
import { SocketType } from '../../../interfaces/SocketType';
import { ValidationError, ValidLightingSetup } from '../../../interfaces/Validation';
import { ArticleDefinitionMapper } from '../../../interfaces/mappers/ArticleDefinitionMapper';
import { ColorMapper } from '../../../interfaces/mappers/ColorMapper';
import {
  ConnectableSlotsAndRequiresConnectArticleMapper,
  ConnectableSlotsMapper,
} from '../../../interfaces/mappers/ConnectableSlotsMapper';
import { MenuProductMapper } from '../../../interfaces/mappers/MenuProductMapper';
import RuleViolationMapper from '../../../interfaces/mappers/RuleViolationMapper';
import RuleViolationWithActionMapper from '../../../interfaces/mappers/RuleViolationWithActionMapper';
import {
  SingleSlotWithParentArticleNumber,
  SingleSlotWithParentArticleNumberMapper,
} from '../../../interfaces/mappers/SingleSlotWithParentArticleNumberMapper';
import { ValidLightingSetupMapper } from '../../../interfaces/mappers/ValidLightingSetupMapper';
import { BackendHttpError } from '../../../services/axiosConfig';
import { mapApiError } from './apiErrorMapping';
import { articleNumberUrlEncoding } from './articleNumberUrlEncoding';

export const getPreferredSlot = async (
  articleNumber: string,
  availableSlots: SingleSlotWithParentArticleNumber[],
  usedSlots: BlockedSlot[],
): Promise<ConnectableSlotsAndRequiresConnectArticle | Error> => {
  const request: CoreServiceAPI.GetPreferredSlotBody = {
    blockedSingleSlotInstances: usedSlots.map(BlockedSlotMapper.toDto),
    availableSlots: availableSlots.map(SingleSlotWithParentArticleNumberMapper.toDto),
  };

  const response:
    | AxiosResponse<CoreServiceAPI.ConnectableSlotsDto>
    | AxiosError<BackendHttpError> = await axios.put(
    `${coreServiceUrl}/v2/products/articles/${articleNumberUrlEncoding(articleNumber)}/slots`,
    request,
  );

  if (response instanceof Error) {
    return response.response ? mapApiError(response.response.data) : new Error();
  }

  return ConnectableSlotsAndRequiresConnectArticleMapper.toDomain(response.data);
};

export const getRequiredConnectArticles = (
  articleNumber: string,
  body: CoreServiceAPI.SlotDefinitionDto,
): Promise<AxiosResponse<CoreServiceAPI.RequiredConnectArticleDto>> =>
  axios.put(
    `${coreServiceUrl}/v2/products/articles/${articleNumberUrlEncoding(
      articleNumber,
    )}/required-connect-articles`,
    body,
  );

export const getFittingArticle = (
  productNumber: string,
  body: CoreServiceAPI.GetFittingArticleForRootBody,
): Promise<AxiosResponse<CoreServiceAPI.ArticleDto>> =>
  axios.put(`${coreServiceUrl}/v2/products/${productNumber}/fitting-article`, body);

export const getAllProvidedSlots = (articleNumber: string) => {
  return axios.get(
    `${coreServiceUrl}/v2/products/articles/${articleNumberUrlEncoding(articleNumber)}/slots`,
  );
};

export const getAllCategories = async (): Promise<Category[]> => {
  const response = await axios.get(`${coreServiceUrl}/v2/products/categories`);

  if (!response || response.status !== 200) {
    throw new Error(`getAllCategories request is not successful`);
  }

  return response.data;
};

export const getPossibleSingleSlotsToMoveArticle = async (
  articleNumber: string,
  availableSlots: SingleSlotWithParentArticleNumber[],
  usedSlots: BlockedSlot[],
): Promise<ConnectableSlots[]> => {
  const request: CoreServiceAPI.GetAllCombinationsOfAvailableSlotsBody = {
    availableSlotInstances: availableSlots.map(SingleSlotWithParentArticleNumberMapper.toDto),
    blockedSingleSlotInstances: usedSlots.map(BlockedSlotMapper.toDto),
  };

  const response: AxiosResponse<CoreServiceAPI.ConnectableSlotsDto[]> = await axios.put(
    `${coreServiceUrl}/v2/products/articles/${articleNumberUrlEncoding(
      articleNumber,
    )}/possible-connecting-slots`,
    request,
  );

  if (!response || response.status !== 200) {
    throw new Error(
      `getPossibleSingleSlotsToMoveArticle request for article number: ${articleNumber} is not successful`,
    );
  }

  return response.data.map(ConnectableSlotsMapper.toDomain);
};

export const validate = async (
  articleNumbers: string[],
  articleNumbersTree: Map<string, string[]>,
): Promise<ValidationError[] | Error> => {
  const body: CoreServiceAPI.ValidateConfigurationBody = { articleNumbers, articleNumbersTree };
  const response: AxiosResponse<CoreServiceAPI.ValidationResultDto> = await axios.put(
    `${coreServiceUrl}/v2/configurations/validate`,
    body,
  );
  if (!response || response.status !== 200) {
    return new Error(`validateConfiguration request for current configuration is not successful`);
  }
  return response.data.ruleViolations.map(RuleViolationMapper.toDomain);
};

export const getValidLightingSetup = async (
  articles: BaseArticle[],
  socketType: SocketType,
): Promise<ValidLightingSetup[] | Error> => {
  const idAndNumberPairs = articles.map((article) => ({
    articleId: article.id,
    articleNumber: article.number,
  }));
  const body: CoreServiceAPI.GetValidLightingSetupBody = {
    articles: idAndNumberPairs,
    socketType,
  };
  const response = await axios.put(`${coreServiceUrl}/v2/configurations/lighting`, body);

  if (!response || response.status !== 200) {
    return new Error(`getValidLightingSetup request for current configuration is not successful`);
  }

  return response.data.lightingRelatedArticles.map(ValidLightingSetupMapper.toDomain);
};

export const getActionsForValidationErrors = async (
  articleNumbers: string[],
  errors: ValidationError[],
): Promise<ValidationError[] | Error> => {
  const body: CoreServiceAPI.GetFixActionsForConfigurationBody = {
    articleNumbers,
    validationResults: errors.map(RuleViolationMapper.toDto),
  };

  const response = await axios.put(
    `${coreServiceUrl}/v2/configurations/validation/user-actions`,
    body,
  );

  if (!response || response.status !== 200) {
    return new Error(
      `getActionsForValidationErrors request for current configuration is not successful`,
    );
  }

  return response.data.map(RuleViolationWithActionMapper.toDomain);
};

export const getAllProductsForCategory = async (
  productCategoryKey: string,
): Promise<MenuProduct[]> => {
  const response = await axios.get(
    `${coreServiceUrl}/v2/categories/${productCategoryKey}/products`,
  );
  if (!response || response.status !== 200) {
    throw new Error(
      `Failed to retrieve products for category '${productCategoryKey}': ${response}`,
    );
  }

  return response.data.map(MenuProductMapper.toDomain);
};

export const getRootDefaultArticles = async (): Promise<ArticleDefinition[]> => {
  const response = await axios.get(`${coreServiceUrl}/v2/products/root-defaults`);

  if (!response || response.status !== 200) {
    throw new Error(`getRootDefaultArticles request is not successful`);
  }

  return response.data.map(ArticleDefinitionMapper.toDomain);
};

export const getColor = async (articleNumber: string): Promise<Color[]> => {
  const response = await axios.get(
    `${coreServiceUrl}/v2/products/articles/${articleNumberUrlEncoding(articleNumber)}/colors`,
  );

  if (!response || response.status !== 200) {
    throw new Error(`Could not load colors for article ${articleNumber}`);
  }

  return response.data.map(ColorMapper.toColorDomain);
};

export const getImages = async (articleNumber: string): Promise<ArticleImage[]> => {
  const response = await axios.get(
    `${coreServiceUrl}/v2/products/articles/${articleNumberUrlEncoding(
      articleNumber,
    )}/media-assets`,
  );

  if (!response || response.status !== 200) {
    throw new Error(`Could not load media assets for article ${articleNumber}`);
  }

  return response.data;
};

export const getArticleDependentOnRoot = async (
  previousArticleNumber: string,
  rootArticleNumber: string,
): Promise<ArticleDefinition> => {
  const request: CoreServiceAPI.GetArticleDependingOnRootBody = {
    previousArticleNumber: previousArticleNumber,
    rootArticleNumber: rootArticleNumber,
  };

  const response = await axios.put(
    `${coreServiceUrl}/v2/products/articles/root-dependent`,
    request,
  );

  if (!response || response.status !== 200) {
    throw new Error(
      `Could not get article for previous article ${previousArticleNumber} and root ${rootArticleNumber}`,
    );
  }

  return ArticleDefinitionMapper.toDomain(response.data);
};
